import { Box } from '@mui/system'
import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Button, ClickAwayListener, Divider, Grow, MenuItem, MenuList, Paper, Popper } from '@mui/material'
import MoreHorizIcon from '@mui/icons-material/MoreHoriz'
import FiberManualRecordRoundedIcon from '@mui/icons-material/FiberManualRecordRounded'
import EditIcon from '@mui/icons-material/Edit'
import { editCompany, retrieveCompanies, retrieveCompaniesModules } from '../../redux/features/companies/companiesThunk'
import ConfirmationModalContainer from '../Modals/ConfirmationModalContainer.jsx'
import CompanyModal from '../Modals/CompanyModal.jsx'
import FinishModalContainer from '../Modals/FinishModalContainer.jsx'

const CompanyActionBtn = ({ data, status, statusButtonList }) => {
  const dispatch = useDispatch()

  const [formModalState, setFormModalState] = useState(false)
  const [finishModalState, setFinishModalState] = useState(false)
  const [confirmationModalState, setConfirmationModalState] = useState(false)
  const [confirmationData, setConfirmationData] = useState(null)
  const [popperStatus, setPopperStatus] = useState(false)
  const anchorRef = useRef(null)
  const prevOpen = useRef(popperStatus)

  const handleToggle = () => {
    setPopperStatus(!popperStatus)
  }

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return
    }

    setPopperStatus(false)
  }

  function handleListKeyDown(event) {
    if (event.key === 'Tab') {
      event.preventDefault()
      setPopperStatus(false)
    }
  }

  useEffect(() => {
    if (prevOpen.current === true && popperStatus === false) {
      dispatch(retrieveCompaniesModules())
      anchorRef.current.focus()
    }

    prevOpen.current = popperStatus
  }, [popperStatus])

  const openFormModal = () => {
    setFormModalState(true)
  }

  const changeStatusHandler = (item) => {
    setConfirmationData(item)
    setConfirmationModalState(true)
  }

  const finishHandler = () => {
    dispatch(retrieveCompanies())
    setFinishModalState(false)
  }

  return (
    <>
      <Box sx={styles.changeStatusContainer}>
        <Button
          ref={anchorRef}
          aria-controls={popperStatus ? 'menu-list-grow' : undefined}
          aria-haspopup='true'
          sx={styles.moreActionsButton}
          onClick={handleToggle}>
          <MoreHorizIcon />
        </Button>

        <Popper
          open={popperStatus}
          anchorEl={anchorRef.current}
          role={undefined}
          transition
          disablePortal
          sx={styles.changeStatusPopper}>
          {({ TransitionProps, placement }) => (
            <Grow
              {...TransitionProps}
              style={{ transformOrigin: placement === 'bottom' ? 'center top' : 'center bottom' }}>
              <Paper sx={styles.popperPaper} onClick={handleClose}>
                <ClickAwayListener onClickAway={handleClose}>
                  <MenuList
                    autoFocusItem={popperStatus}
                    id='menu-list-grow'
                    onKeyDown={handleListKeyDown}>
                    <MenuItem onClick={openFormModal} sx={styles.option}>
                      <EditIcon />
                    Edit Company
                    </MenuItem>

                    <Divider sx={styles.optionDivider} />

                    <MenuItem disabled sx={styles.optionsTitle}>
                    Change Status To
                    </MenuItem>

                    {statusButtonList
                      ?.filter((item) => item.title !== status?.title)
                      .map((item, index) => (
                        <MenuItem
                          key={index}
                          onClick={() => changeStatusHandler(item)}
                          sx={styles.option}>
                          <FiberManualRecordRoundedIcon style={{ fill: `${item.color}` }} />
                          {item.title}
                        </MenuItem>
                      ))}
                  </MenuList>
                </ClickAwayListener>
              </Paper>
            </Grow>
          )}
        </Popper>
      </Box>

      <CompanyModal
        actionToDispatch={editCompany}
        modalTitle='Edit Company'
        modalMessage={`${data.company.name} has been edited successfully`}
        data={data}
        formState={formModalState}
        setFormState={setFormModalState}
        disableButton
        disableIntegrationType
      />
      <ConfirmationModalContainer
        modalState={confirmationModalState}
        setModalState={setConfirmationModalState}
        setFinishModalState={setFinishModalState}
        confirmationData={confirmationData}
        confirmHandler={confirmationData?.confirmHandler}
        confirmHandlerQueries={{
          title: confirmationData?.title,
          data: data?.company,
        }}
      />
      <FinishModalContainer
        modalState={finishModalState}
        modalChangeState={setFinishModalState}
        modalTitle={confirmationData?.title}
        modalMessage={confirmationData?.finish}
        finishHandler={finishHandler}
      />
    </>
  )
}

const styles = {
  changeStatusContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginLeft: 'auto',
    padding: '10px',
    position: { xs: 'absolute', sm: 'unset' },
    top: { xs: '30%', sm: 'unset' },
    right: { xs: '0', sm: 'unset' },
  },
  moreActionsButton: {
    height: { xs: '30px', sm: '40px' },
    minWidth: { xs: '30px', sm: '40px' },
    borderRadius: '12px',
    border: 'solid 2px #e2e8ee',
    backgroundColor: '#f7f9fb',
    color: 'icon.primary',
    overflow: 'hidden',
    padding: '0',
  },
  changeStatusPopper: {
    minWidth: { xs: '100px', sm: '200px' },
    minHeight: { xs: '20px', sm: 'unset' },
    zIndex: '9',
  },
  popperPaper: {
    borderRadius: '12px',
  },
  optionDivider: {
    height: '1px',
    margin: { xs: '4px 8px', sm: '8px 16px' },
    borderRadius: '12px',
    backgroundColor: '#eaeaea',
  },
  option: {
    fontSize: { xs: '10px', sm: '14px' },
    padding: { xs: '0px 6px', sm: '6px 16px' },
    minHeight: { xs: '28px', sm: '48px' },
    color: '#000000',
    textTransform: 'capitalize',
    '& svg': {
      width: { xs: '0.5em', sm: '1em' },
    },
    '& .MuiSvgIcon-root': {
      marginRight: { xs: '5px', sm: '7px' },
    },
  },
  optionsTitle: {
    fontFamily: 'Avenir',
    fontSize: { xs: '10px', sm: '14px' },
    textTransform: 'capitalize',
    padding: { xs: '0px 6px', sm: '6px 16px' },
    minHeight: { xs: '28px', sm: '48px' },
  },
  formModalBtnsContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    margin: '20px -15px 0 -15px',
    padding: '20px 15px 0',
    borderTop: '1px solid #cbcbcb',
  },
}

export default CompanyActionBtn
